import * as React from "react";
import LoginNavBar from "./components/Navbar/LoginNavBar";

import { useAuth } from "./components/contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Button, Checkbox, Form, Input, message } from "antd";
import { getFunctions, httpsCallable } from "firebase/functions";
import ReCAPTCHA from "react-google-recaptcha";
import GoogleLoginInButton from "./components/Media/GoogleLoginInButton";
import { RECAPTCHA_SITE_KEY } from "./recaptchaSiteKey";

export default function SignIn() {
  const { login, currentUser } = useAuth();
  const [loading, setLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState();
  const [isFetched, setIsFetched] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Google Recaptcha
  const [recaptcha, setRecaptcha] = React.useState(null);
  const recaptchaRef = React.useRef(null);
  const getFunctionsInstance = getFunctions();
  const verifyRecaptcha = httpsCallable(
    getFunctionsInstance,
    "verifyRecaptcha"
  );

  const [showRedirected, setShowRedirected] = React.useState(false);
  const [showResetPassword, setShowResetPassword] = React.useState(false);

  const onRecaptchaChange = (value) => setRecaptcha(value);

  async function onFinish(values) {
    setLoading(true);
    verifyRecaptcha({ recaptchaToken: recaptcha })
      .then(async () => {
        try {
          await login(values.email, values.password)
            .then(() => setLoading(false))
            .catch((error) => {
              message.error("Failed to login. Please try again.");
              setErrorMessage(error?.message || String(error));
              recaptchaRef.current?.reset();
              setLoading(false);
            });
        } catch (error) {
          setErrorMessage(error?.message || String(error));
          message.error("Failed to login. Please try again.");
          recaptchaRef.current?.reset();
          setLoading(false);
        }
      })
      .catch(() => {
        message.error("Failed to verify recaptcha. Please try again.");
        setErrorMessage("Failed to verify recaptcha. Please try again.");
        recaptchaRef.current?.reset();
        setLoading(false);
      });

    // Clear transient state
    setTimeout(() => {
      setLoading(false);
      setErrorMessage("");
    }, 2000);
  }

  const onFinishFailed = () => {
    setLoading(true);
    recaptchaRef.current?.reset();
    setLoading(false);
  };

  function handleResetPasswordRequest(e) {
    e.preventDefault();
    if (isFetched) navigate("/auth/reset-password-request");
  }

  function handleDontHaveAccountClick(e) {
    e.preventDefault();
    if (isFetched) navigate("/sign_up");
  }

  React.useEffect(() => {
    if (currentUser !== undefined) setIsFetched(true);
  }, [currentUser]);

  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setShowRedirected(searchParams.get("redirected") === "login");
    setShowResetPassword(searchParams.get("resetPassword") === "true");
  }, [location]);

  return (
    <>
      {/* Spacer so content never hides behind fixed navbar */}
      <div aria-hidden className="h-20 sm:h-22 md:h-24" />

      {/* Top-right alerts */}
      {showRedirected && (
        <Alert
          message="You have been redirected"
          description="Please login to continue."
          type="info"
          showIcon
          style={{ position: "fixed", top: 16, right: 16, zIndex: 1000 }}
          closable
          onClose={() => setShowRedirected(false)}
        />
      )}
      {showResetPassword && (
        <Alert
          message="Password Reset"
          description="Please login with your new password."
          type="info"
          showIcon
          style={{ position: "fixed", top: 16, right: 16, zIndex: 1000 }}
          closable
          onClose={() => setShowResetPassword(false)}
        />
      )}

      {/* Page background */}
      <div className="relative min-h-[calc(100vh-6rem)] bg-slate-50">
        {/* Soft gradient blobs for subtle flair */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden"
        >
          <div className="absolute -top-24 -left-24 h-56 w-56 rounded-full bg-rose-200/50 blur-3xl" />
          <div className="absolute -bottom-20 -right-16 h-64 w-64 rounded-full bg-emerald-200/50 blur-3xl" />
        </div>

        {/* Centered card */}
        <div className="relative z-10 mx-auto flex max-w-7xl items-center justify-center px-4 sm:px-6 lg:px-8 py-10">
          <div className="w-full max-w-md">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-6 shadow-xl backdrop-blur-sm sm:p-8">
              {/* Brand header */}
              <div className="mb-6 text-center">
                <h1 className="text-2xl font-bold tracking-tight text-rose-500 sm:text-3xl">
                  Welcome back
                </h1>
                <p className="mt-1 text-sm text-slate-600">
                  Sign in to continue to Gentle Gourmet
                </p>
              </div>

              {/* Social sign-in */}
              <div className="mb-5">
                <GoogleLoginInButton />
              </div>

              {/* Divider */}
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="h-px w-full bg-slate-200" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-3 text-xs font-medium uppercase tracking-wide text-slate-400">
                    Or continue with email
                  </span>
                </div>
              </div>

              {/* Error inline */}
              {errorMessage ? (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {String(errorMessage)}
                </div>
              ) : null}

              {/* Form */}
              <Form
                name="login"
                layout="vertical"
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                autoComplete="off"
                className="space-y-3"
              >
                <Form.Item
                  label={
                    <span className="text-sm font-medium text-slate-700">
                      Email
                    </span>
                  }
                  name="email"
                  rules={[
                    { required: true, message: "Please input your email!" },
                  ]}
                >
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    className="h-11 rounded-xl border-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <span className="text-sm font-medium text-slate-700">
                      Password
                    </span>
                  }
                  name="password"
                  rules={[
                    { required: true, message: "Please input your password!" },
                  ]}
                >
                  <Input.Password
                    placeholder="••••••••"
                    className="h-11 rounded-xl border-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                </Form.Item>

                {/* reCAPTCHA */}
                {RECAPTCHA_SITE_KEY ? (
                  <div className="my-2 flex justify-center">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={onRecaptchaChange}
                    />
                  </div>
                ) : null}

                {/* Remember + actions */}
                <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <Form.Item name="remember" valuePropName="checked" noStyle>
                    <Checkbox>Remember me</Checkbox>
                  </Form.Item>

                  <button
                    onClick={handleResetPasswordRequest}
                    className="self-start text-sm font-semibold text-blue-600 hover:text-blue-700"
                  >
                    Forgot password?
                  </button>
                </div>

                <Button
                  type="default"
                  htmlType="submit"
                  className="mt-1 h-11 w-full rounded-xl bg-blue-600 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-500/20 disabled:opacity-50"
                  disabled={loading}
                  loading={loading}
                >
                  Sign In
                </Button>
              </Form>

              {/* Footer links */}
              <div className="mt-6 flex items-center justify-center gap-1 text-sm">
                <span className="text-slate-600">
                  Don&apos;t have an account?
                </span>
                <button
                  onClick={handleDontHaveAccountClick}
                  className="font-semibold text-emerald-600 hover:text-emerald-700"
                >
                  Sign up
                </button>
              </div>
            </div>

            {/* Tiny brand footer */}
            <div className="mt-6 text-center text-xs text-slate-500">
              © {new Date().getFullYear()} Gentle-Gourmet. All rights reserved.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
